import { call, fork, takeEvery, put, select, all } from 'redux-saga/effects';
import * as api from '../requesters/scoutingRequester';
import * as apiNew from '../requesters/scoutingRequesterNew'
import { inverseCoordinates as inverse } from '../utils/geo';
import { logDmbErrorLocation } from '../utils/error';
import {
  failureNewActivity,
  receiveNewActivity,
  failureActivities,
  receiveActivities,
  receiveDeleteActivity,
  failureDeleteActivity,
  receiveTags,
  failureTags,
  receiveCategories,
  failureCategories,
  receiveUpdateActivity,
  failureUpdateActivity,
  failureUpdateEnhancedActivity,
  receiveDeleteAsset,
  failureDeleteAsset,
  receiveDeleteComments,
  requestActivity,
  editActivity,
  failureActivity,
} from '../actions/scoutingActions';
import { WEB_DMB_ENHANCED_SCOUTING } from "../constants/featureFlags";
import {
  REQUEST_NEW_ACTIVITY,
  REQUEST_ACTIVITIES,
  REQUEST_DELETE_ACTIVITY,
  REQUEST_TAGS,
  REQUEST_CATEGORY,
  REQUEST_UPDATE_ACTIVITY,
  REQUEST_UPDATE_ENHANCED_ACTIVITY,
  REQUEST_DELETE_ASSET,
  REQUEST_DELETE_COMMENTS,
  REQUEST_ACTIVITY,
} from '../constants/actionTypes';
import { trackEvent } from '../actions/analyticsActions';
import { closeModal } from '../actions/modalActions';
import { getAnalyticsParamsFromActivity } from '../utils/analytics';
import { flipActivityCoordinates, getActivitySeasonYear, isFile } from '../utils/scoutingUtils';

/**
 * Save a FRR comment
 * @param {Object} action
 * @return {void}
 */
export function* postComment(activityId, note) {
  try {
    yield call(apiNew.sendComment, activityId, note);
  } catch (error) {
    logDmbErrorLocation(error, 'post comment scouting saga');
  }
}

/**
 * Delete a list of FRR comments
 * @param {Object} action
 * @return {void}
 */
export function* deleteComments(action) {
  const { activityId, commentsIds, index } = action.payload;
  try {
    yield all(
      commentsIds.map((commentId) =>
        call(apiNew.removeComment, activityId, commentId)
      )
    );
  } catch (error) {
    logDmbErrorLocation(error, "delete comment scouting saga");
  } finally {
    yield put(requestActivity(activityId));
    yield put(receiveDeleteComments());
    yield put(closeModal(index));
  }
}

/**
 * Get pins API call
 * @param {String} field
 * @return {void}
 */
export function* getActivities(field, useTenderfootV3) {
  try {
    // Get the activities from API
    const response = yield call(useTenderfootV3 ? apiNew.requestActivities : api.requestActivities, field);

    // Flip coordinates
    const activities = response.data.map((activity) => {
      if (!activity.location) {
        return activity;
      }

      return flipActivityCoordinates(activity);
    });

    // Separate activities into seasons
    return activities.reduce((acc, activity) => {
      const season = getActivitySeasonYear(activity, activity.scoutedAt);
      acc[season] = acc[season] ? acc[season].concat(activity) : [activity];
      return acc;
    }, {});
  } catch (error) {
    return {};
  }
}

/**
 * Get an activity by ID
 * @param {*} action
 */
export function* getActivity(action) {
  const { activityId } = action.payload;

  try {
    const response = yield call(apiNew.requestActivity, activityId);
    const activity = flipActivityCoordinates(response);
    yield put(editActivity(activity));
  } catch (error) {
    logDmbErrorLocation(error, 'fetch activity scouting saga');
    yield put(failureActivity());
  }
}

export function* postActivity(action) {
  try {
    // Send activities to API
    const activity = action.payload;
    let response = yield call(action.enhanced ? apiNew.sendActivity : api.sendActivity, activity);

    if (response.location) {
      response = flipActivityCoordinates(response);
    }

    if (action.asset) {
      const assetResponse = yield call(
        api.sendAsset,
        action.asset,
        response.id
      );
      response.assets = assetResponse.assets;
    }
    yield put(receiveNewActivity(response));
    yield put(closeModal(activity.index));
    if (!response.location) {
      // Field note
      yield put(
        trackEvent('field_note', 'created', {
          fieldNoteUuid: response.id,
          ...getAnalyticsParamsFromActivity(response),
        })
      );
    } else if (response.location.type === 'Point') {
      // Pin
      yield put(
        trackEvent('scouting_pin', 'created', {
          pinUuid: response.id,
          ...getAnalyticsParamsFromActivity(response),
        })
      );
    } else {
      // Region
      yield put(
        trackEvent('region_note', 'created', {
          regionNoteUuid: response.id,
          ...getAnalyticsParamsFromActivity(response),
        })
      );
      yield postComment(response.id, activity.comments[0].note);
    }
  } catch (error) {
    logDmbErrorLocation(error, 'scouting saga');
    yield put(failureNewActivity());
  }
}

export function* deleteActivity(action) {
  try {
    const activity = action.payload;
    yield call(api.deleteActivity, activity.id);
    yield put(receiveDeleteActivity(activity));
    yield put(closeModal(action.meta.index));
    if (!activity.location) {
      // Field note
      yield put(
        trackEvent('field_note', 'deleted ', {
          fieldNoteUuid: activity.id,
          ...getAnalyticsParamsFromActivity(activity),
        })
      );
    } else if (activity.location.type === 'Point') {
      // Pin
      yield put(
        trackEvent('scouting_pin', 'deleted ', {
          pinUuid: activity.id,
          ...getAnalyticsParamsFromActivity(activity),
        })
      );
    } else {
      // Region
      yield put(
        trackEvent('region_note', 'deleted ', {
          regionNoteUuid: activity.id,
          ...getAnalyticsParamsFromActivity(activity),
        })
      );
    }
  } catch (error) {
    logDmbErrorLocation(error, 'scouting saga');
    yield put(failureDeleteActivity(error));
  }
}

export function* fetchActivities(action) {
  try {
    const { feature } = yield select();
    const useTenderfootV3 = feature.userConfigFeatures[WEB_DMB_ENHANCED_SCOUTING]?.value;
    const response = yield call(getActivities, action.fieldUUID, useTenderfootV3);
    yield put(receiveActivities(response));
  } catch (error) {
    logDmbErrorLocation(error, 'fetch scouting activities saga');
    yield put(failureActivities());
  }
}

export function* fetchTags() {
  try {
    const { user } = yield select();
    const response = yield call(api.requestTags, user.id);
    yield put(receiveTags(response));
  } catch (error) {
    logDmbErrorLocation(error, 'fetch scouting tags saga');
    yield put(failureTags());
  }
}

export function* fetchCategories() {
  try {
    const response = yield call(apiNew.requestCategory);
    yield put(receiveCategories(response));
  } catch (error) {
    logDmbErrorLocation(error, 'fetch scouting observations saga');
    yield put(failureCategories());
  }
}

export function* updateActivity(action) {
  try {
    const { activity, comment, assets, permanent, isNewComment } = action.payload;
    let response = yield call(api.updateActivity, activity);

    let commentResponse = null;
    if (isNewComment) {
      commentResponse = yield call(
        apiNew.sendComment,
        activity.id,
        comment.note
      );
    } else {
      commentResponse = yield call(
        api.updateComments,
        activity.id,
        comment
      );
    }

    response.comments = [commentResponse];

    if (response.location) {
      response = {
        ...response,
        inversedLocation: {
          ...response.location,
          coordinates: inverse(response.location.coordinates),
        },
      };
    }

    if (assets.newAsset) {
      const assetResponse = yield call(
        api.sendAsset,
        assets.newAsset,
        activity.id
      );
      response.assets = assetResponse.assets;
    } else if (assets.oldAsset) {
      response.assets = [assets.oldAsset];
    }

    yield put(receiveUpdateActivity(response, permanent));
    yield put(closeModal(activity.index));
  } catch (error) {
    logDmbErrorLocation(error, 'update activity saga');
    yield put(failureUpdateActivity());
  }
}

export function* updateEnhancedActivity(action) {
  const { activity, comment, assets, permanent } = action.payload;

  try {
    let response = yield call(apiNew.updateActivity, activity);

    if (response.location) {
      response = {
        ...response,
        inversedLocation: {
          ...response.location,
          coordinates: inverse(response.location.coordinates),
        },
      };
    }

    let commentResponse = null;
    if (comment.id) {
      commentResponse = yield call(
        api.updateComments,
        activity.id,
        comment
      );
    } else {
      commentResponse = yield call(
        apiNew.sendComment,
        activity.id,
        comment.note
      );
    }

    response.comments = [commentResponse];

    const assetsToCreate = assets.filter(isFile);
    const assetsToDelete = assets.filter(file => file.deleted);
    let newAssets = assets.filter(file => !(isFile(file) || file.deleted));

    if (assetsToCreate.length > 0) {
      const assetResponse = yield call(
        api.sendAsset,
        assetsToCreate,
        activity.id
      );
      newAssets = [...newAssets, ...assetResponse.assets];
    }

    if (assetsToDelete.length > 0) {
      const { user } = yield select();
      yield all(assetsToDelete.map(file => call(api.deleteAsset, activity.id, file.id, user.id)));
    }

    response.assets = newAssets;

    yield put(receiveUpdateActivity(response, permanent));
    yield put(closeModal(activity.index));
  } catch (error) {
    yield put(failureUpdateEnhancedActivity());
  }
}

export function* deleteAsset(action) {
  try {
    const { user } = yield select();
    const { activityId, assetId } = action.payload;
    yield call(api.deleteAsset, activityId, assetId, user.id);
    yield put(receiveDeleteAsset());
  } catch (error) {
    logDmbErrorLocation(error, 'scouting saga');
    yield put(failureDeleteAsset(error));
  }
}

export function* postActivityWatcher() {
  yield takeEvery(REQUEST_NEW_ACTIVITY, postActivity);
}

export function* deleteActivityWatcher() {
  yield takeEvery(REQUEST_DELETE_ACTIVITY, deleteActivity);
}

export function* getActivitiesWatcher() {
  yield takeEvery(REQUEST_ACTIVITIES, fetchActivities);
}

export function* getActivitiyWatcher() {
  yield takeEvery(REQUEST_ACTIVITY, getActivity);
}

export function* getTagsWatcher() {
  yield takeEvery(REQUEST_TAGS, fetchTags);
}

export function* getCategoriesWatcher() {
  yield takeEvery(REQUEST_CATEGORY, fetchCategories);
}

export function* updateActivityWatcher() {
  yield takeEvery(REQUEST_UPDATE_ACTIVITY, updateActivity);
}

export function* updateEnhancedActivityWatcher() {
  yield takeEvery(REQUEST_UPDATE_ENHANCED_ACTIVITY, updateEnhancedActivity);
}

export function* deleteAssetWatcher() {
  yield takeEvery(REQUEST_DELETE_ASSET, deleteAsset);
}

export function* deleteCommentsWatcher() {
  yield takeEvery(REQUEST_DELETE_COMMENTS, deleteComments);
}

export default function* rootSaga() {
  yield fork(getActivitiesWatcher);
  yield fork(getActivitiyWatcher);
  yield fork(postActivityWatcher);
  yield fork(deleteActivityWatcher);
  yield fork(getTagsWatcher);
  yield fork(getCategoriesWatcher);
  yield fork(updateActivityWatcher);
  yield fork(updateEnhancedActivityWatcher);
  yield fork(deleteAssetWatcher);
  yield fork(deleteCommentsWatcher);
}
